/**
 * Runtime validation for level data.
 *
 * `contracts/` is otherwise types and constants, and TAD-001 says "if it grows a
 * function, ask why". The answer, from TAD-001's level-loading section: the
 * validator is the runtime half of `level.ts`, and separating the two invites
 * exactly the drift the validator exists to catch. It resolves SDD-006 open
 * question 1 — hand-written guards, not a schema library, because the schema is
 * eleven fields and a discriminated union while the shell budget is 60 kB.
 *
 * Three properties are deliberate.
 *
 * It reports *every* fault rather than the first. `DATA-049` asks for enough
 * detail to diagnose a refusal, and a validator that stops at the first fault
 * turns one bad level into as many edit-run cycles as it has faults.
 *
 * It builds its result rather than asserting one. Nothing here says
 * `value as Level`; the returned object is assembled field by field from values
 * already checked, so the type is earned instead of claimed. Unknown top-level
 * keys are dropped, which is the point — anything a game needs belongs in
 * `rules`, and that is `DATA-008`.
 *
 * It is total. No throws, no `any`. Callers get a value they must destructure,
 * so "I forgot to check" is a type error rather than a board built from a
 * half-read file.
 */

import { LEVEL_SCHEMA_VERSION, goalSubsumes } from './level';
import type { Constraints, Goal, GridSize, Level, LevelIndex, LevelMode } from './level';

/** One thing wrong, located well enough to find it in the file. */
export interface Fault {
  /** Dotted path from the document root, e.g. `goals[1].count`. */
  path: string;
  problem: string;
}

export type Validated<T> = { ok: true; value: T } | { ok: false; faults: Fault[] };

/**
 * Why a document was refused on version grounds. Kept separate from `Fault`
 * because the player-facing message differs: `too-new` means the application is
 * stale and should be updated, which is actionable, while a malformed field
 * means the data is broken, which is not. `DATA-047` requires the shell to tell
 * those apart, so the validator must not flatten them into one failure.
 */
export type VersionVerdict = 'ok' | 'too-new' | 'too-old' | 'unreadable';

function majorOf(version: string): number | null {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(version);
  if (match?.[1] === undefined) return null;
  return Number(match[1]);
}

const CURRENT_MAJOR = majorOf(LEVEL_SCHEMA_VERSION);

/**
 * `DATA-042` and `DATA-043`. Refuse in both directions; TAD-001 resolved
 * SDD-006 open question 2 against runtime migration, on the grounds that levels
 * ship with the build, so a version mismatch can only come from a stale service
 * worker cache — a path that runs almost never and is therefore never right.
 */
export function checkSchemaVersion(version: unknown): VersionVerdict {
  if (typeof version !== 'string') return 'unreadable';
  const major = majorOf(version);
  if (major === null || CURRENT_MAJOR === null) return 'unreadable';
  if (major > CURRENT_MAJOR) return 'too-new';
  if (major < CURRENT_MAJOR) return 'too-old';
  return 'ok';
}

/**
 * What each mode implies about ordering and termination.
 *
 * `DATA-012` allows an index of 0 only in a mode that defines no ordering, and
 * `DATA-013` allows a level with neither a move limit nor a timer only in a mode
 * that defines play as unbounded. Both rules are stated in terms of the mode, so
 * the mode's properties have to live somewhere — here, once, rather than as a
 * scatter of `mode === 'freeplay'` comparisons that drift apart.
 *
 * Writing it as a table rather than as a comparison at each call site is what
 * made SDD-006 open question 3 answerable: `'daily'` needed a row here, nobody
 * knew what belonged in it, and that was the argument for removing the mode.
 * A new mode has to answer both columns before it can exist.
 */
const MODE_TRAITS: Record<LevelMode, { ordered: boolean; bounded: boolean }> = {
  adventure: { ordered: true, bounded: true },
  freeplay: { ordered: false, bounded: false },
};

const MODES = Object.keys(MODE_TRAITS) as LevelMode[];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * `Array.isArray` narrows `unknown` to `any[]`, and an `any` leaking out of the
 * one function whose job is to eliminate them would put every subsequent element
 * access beyond the reach of the type checker. This narrows to `unknown[]`.
 */
function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'an array';
  if (typeof value === 'string') return `the string ${JSON.stringify(value)}`;
  if (typeof value === 'number') return `the number ${String(value)}`;
  return `a value of type ${typeof value}`;
}

/**
 * Collects faults and returns checked values or `null`.
 *
 * The checkers return the value rather than acting as type predicates. A
 * predicate cannot narrow `record.someField` reliably, so a predicate-shaped API
 * forces a cast at every call site, and a validator whose implementation is
 * mostly casts is not obviously doing anything.
 */
class Checker {
  readonly faults: Fault[] = [];

  add(path: string, problem: string): void {
    this.faults.push({ path, problem });
  }

  /** Non-empty. An empty title renders as a blank tile rather than an error. */
  text(value: unknown, path: string): string | null {
    if (typeof value !== 'string') {
      this.add(path, `expected a string, got ${describe(value)}`);
      return null;
    }
    if (value.trim() === '') {
      this.add(path, 'expected a non-empty string');
      return null;
    }
    return value;
  }

  int(value: unknown, path: string, min: number, max = Number.MAX_SAFE_INTEGER): number | null {
    if (typeof value !== 'number' || !Number.isInteger(value)) {
      this.add(path, `expected an integer, got ${describe(value)}`);
      return null;
    }
    if (value < min || value > max) {
      this.add(path, `expected an integer in ${String(min)}..${String(max)}, got ${String(value)}`);
      return null;
    }
    return value;
  }

  mode(value: unknown, path: string): LevelMode | null {
    if (typeof value === 'string' && (MODES as string[]).includes(value)) {
      return value as LevelMode;
    }
    this.add(path, `expected one of ${MODES.join(', ')}; got ${describe(value)}`);
    return null;
  }
}

function checkGrid(value: unknown, path: string, c: Checker): GridSize | null {
  if (!isRecord(value)) {
    c.add(path, `expected an object, got ${describe(value)}`);
    return null;
  }
  // Three is the smallest board a run can exist on. A 2x2 grid is not a
  // degenerate level, it is a level that can never be completed, and refusing it
  // here is cheaper than diagnosing it later as a game that never ends.
  // `DATA-074`: nine is the largest board a phone can fit -- width only, the
  // GDD's own rebalance decision (sprint-8a stage 2); rows are uncapped, so
  // a level narrower than it is tall (`match3.adventure.010`, 9x11) is legal.
  const cols = c.int(value.cols, `${path}.cols`, 3, 9);
  const rows = c.int(value.rows, `${path}.rows`, 3);
  if (cols === null || rows === null) return null;
  return { cols, rows };
}

function checkConstraints(value: unknown, path: string, c: Checker): Constraints | null {
  if (!isRecord(value)) {
    c.add(path, `expected an object, got ${describe(value)}`);
    return null;
  }
  // `DATA-007`: zero is a value, not a missing field. Zero moves means unlimited
  // and zero milliseconds means untimed, so the floor is 0 here and the question
  // of whether the level can end at all belongs to `DATA-013`, below.
  const moveLimit = c.int(value.moveLimit, `${path}.moveLimit`, 0);
  const timeLimitMs = c.int(value.timeLimitMs, `${path}.timeLimitMs`, 0);
  if (moveLimit === null || timeLimitMs === null) return null;
  return { moveLimit, timeLimitMs };
}

function checkGoal(value: unknown, path: string, c: Checker): Goal | null {
  if (!isRecord(value)) {
    c.add(path, `expected an object, got ${describe(value)}`);
    return null;
  }

  // `DATA-006`: kinds are named strings. Requiring them to be *known* strings is
  // not a restriction on games — `custom` is the escape hatch that satisfies
  // `DATA-048`, and it carries the label the shell renders when it cannot reason
  // about the goal itself.
  const kind: unknown = value.kind;
  if (typeof kind !== 'string') {
    c.add(`${path}.kind`, `expected a goal kind name, got ${describe(kind)}`);
    return null;
  }

  switch (kind) {
    case 'score': {
      const target = c.int(value.target, `${path}.target`, 1);
      return target === null ? null : { kind: 'score', target };
    }
    case 'collect': {
      const piece = c.text(value.piece, `${path}.piece`);
      const count = c.int(value.count, `${path}.count`, 1);
      return piece === null || count === null ? null : { kind: 'collect', piece, count };
    }
    case 'clear-obstacle': {
      const obstacle = c.text(value.obstacle, `${path}.obstacle`);
      const count = c.int(value.count, `${path}.count`, 1);
      return obstacle === null || count === null
        ? null
        : { kind: 'clear-obstacle', obstacle, count };
    }
    case 'custom': {
      const id = c.text(value.id, `${path}.id`);
      const label = c.text(value.label, `${path}.label`);
      const target = c.int(value.target, `${path}.target`, 1);
      return id === null || label === null || target === null
        ? null
        : { kind: 'custom', id, label, target };
    }
    case 'avoid': {
      // `DATA-072`: a limit of zero is legal ("match none at all") and only
      // negative is rejected -- the floor here is 0, not 1, unlike every
      // other counted goal above.
      const piece = c.text(value.piece, `${path}.piece`);
      const limit = c.int(value.limit, `${path}.limit`, 0);
      return piece === null || limit === null ? null : { kind: 'avoid', piece, limit };
    }
    case 'time': {
      const limitMs = c.int(value.limitMs, `${path}.limitMs`, 0);
      return limitMs === null ? null : { kind: 'time', limitMs };
    }
    default:
      c.add(
        `${path}.kind`,
        `expected one of score, collect, clear-obstacle, custom, avoid, time; got ${describe(kind)}`,
      );
      return null;
  }
}

/**
 * `DATA-069`-`071`, `DATA-073`. `optionalGoals` is an array of goal-sets;
 * each set is checked with the same `checkGoal` the required `goals` array
 * uses, then the set-level rules apply. `required` (the level's own `Goal[]`,
 * not a derived key set -- sprint-8b stage 0) is what `DATA-071`'s
 * subsumption check needs: whether every goal in an optional set is
 * `goalSubsumes`d by some required goal, which is a stricter, decidable
 * question than the `goalKey`-equality this function used to ask. See
 * `goalSubsumes`'s own doc comment (`@contracts/level`) for why equality
 * over-rejected -- a `collect` goal asking for *more* than the required
 * count is a legitimate optional objective, not a vacuous one.
 */
function checkOptionalGoals(
  value: unknown,
  path: string,
  c: Checker,
  required: readonly Goal[],
): Goal[][] | undefined {
  if (value === undefined) return undefined;
  if (!isArray(value)) {
    c.add(path, `expected an array, got ${describe(value)}`);
    return undefined;
  }
  // `DATA-069`: "more optional goal sets than there are stars beyond the
  // first" -- two, phrased against the star count rather than the literal
  // number so the two requirements cannot drift apart silently.
  if (value.length > 2) {
    c.add(path, `expected at most 2 optional goal sets, got ${String(value.length)}`);
  }

  const sets: Goal[][] = [];
  value.forEach((rawSet, i) => {
    const setPath = `${path}[${String(i)}]`;
    if (!isArray(rawSet)) {
      c.add(setPath, `expected an array, got ${describe(rawSet)}`);
      return;
    }
    // `DATA-070`: an empty set awards a star for nothing, the same failure
    // shape `DATA-071` catches by content rather than by size.
    if (rawSet.length === 0) {
      c.add(setPath, 'expected a non-empty optional goal set');
      return;
    }

    const set: Goal[] = [];
    let allSubsumed = true;
    for (const [j, rawGoal] of rawSet.entries()) {
      const goal = checkGoal(rawGoal, `${setPath}[${String(j)}]`, c);
      if (goal === null) {
        allSubsumed = false;
        continue;
      }
      set.push(goal);
      if (!required.some((req) => goalSubsumes(req, goal))) allSubsumed = false;
    }

    // `allSubsumed` starts `true` and only ever turns `false`, so it stays
    // `true` here only if every goal in `rawSet` both parsed and was
    // subsumed by some required goal -- which, since the empty-set return
    // above already guarantees `rawSet.length > 0`, is exactly the case
    // this reports.
    if (allSubsumed) {
      c.add(setPath, 'every goal in this set is already guaranteed by the required goals');
    }
    sets.push(set);
  });

  return sets;
}

/**
 * `DATA-032` and `DATA-044`.
 *
 * The version check is the caller's, via `checkSchemaVersion`. Refusing a level
 * for being from the future needs a different message than refusing it for being
 * malformed, and this function only knows how to produce the second.
 */
export function validateLevel(value: unknown): Validated<Level> {
  const c = new Checker();

  if (!isRecord(value)) {
    c.add('', `expected an object, got ${describe(value)}`);
    return { ok: false, faults: c.faults };
  }

  const id = c.text(value.id, 'id');
  const gameId = c.text(value.gameId, 'gameId');
  const schemaVersion = c.text(value.schemaVersion, 'schemaVersion');
  const title = c.text(value.title, 'title');
  const difficulty = c.int(value.difficulty, 'difficulty', 1, 10);
  const seed = c.int(value.seed, 'seed', 0);
  const mode = c.mode(value.mode, 'mode');

  // `DATA-012`. The floor depends on the mode, so an unresolved mode leaves this
  // unchecked rather than guessing. Reporting `index: expected >= 1` against a
  // file whose actual fault is a typo in `mode` sends the reader to the wrong
  // line, which is worse than reporting one fault instead of two.
  const index =
    mode === null ? null : c.int(value.index, 'index', MODE_TRAITS[mode].ordered ? 1 : 0);

  const grid = checkGrid(value.grid, 'grid', c);
  const constraints = checkConstraints(value.constraints, 'constraints', c);

  // `DATA-013`. The rule that stops a level shipping with no move limit, no
  // timer and no way to end — a state in which every individual field is valid
  // and the level is unplayable.
  if (mode !== null && constraints !== null && MODE_TRAITS[mode].bounded) {
    if (constraints.moveLimit === 0 && constraints.timeLimitMs === 0) {
      c.add(
        'constraints',
        `mode ${mode} defines play as bounded, so one of moveLimit or timeLimitMs must be non-zero`,
      );
    }
  }

  // `DATA-005`: at least one goal. A level with none cannot be won.
  const goals: Goal[] = [];
  const rawGoals = value.goals;
  if (!isArray(rawGoals)) {
    c.add('goals', `expected an array, got ${describe(rawGoals)}`);
  } else if (rawGoals.length === 0) {
    c.add('goals', 'expected at least one goal');
  } else {
    rawGoals.forEach((raw, i) => {
      const goal = checkGoal(raw, `goals[${String(i)}]`, c);
      if (goal !== null) {
        // `DATA-073`: `time` may be an optional goal but never a required
        // one -- completion must never depend on speed. Checked here, not
        // inside `checkGoal`, because the same function also validates
        // optional goals, where `time` is legal.
        if (goal.kind === 'time') {
          c.add(`goals[${String(i)}]`, 'a time goal is not allowed among required goals');
        } else {
          goals.push(goal);
        }
      }
    });
  }

  const optionalGoals = checkOptionalGoals(value.optionalGoals, 'optionalGoals', c, goals);

  // `DATA-008` and `DATA-033`: `rules` belongs to the game, and the shell checks
  // only that it is an object. Looking inside would make the shell know what a
  // spawn table is, which is the boundary this whole schema exists to hold.
  let rules: Record<string, unknown> | undefined;
  if (value.rules !== undefined) {
    if (isRecord(value.rules)) {
      rules = value.rules;
    } else {
      c.add('rules', `expected an object or absence, got ${describe(value.rules)}`);
    }
  }

  if (
    c.faults.length > 0 ||
    id === null ||
    gameId === null ||
    schemaVersion === null ||
    title === null ||
    difficulty === null ||
    seed === null ||
    mode === null ||
    index === null ||
    grid === null ||
    constraints === null
  ) {
    return { ok: false, faults: c.faults };
  }

  return {
    ok: true,
    value: {
      id,
      gameId,
      schemaVersion,
      mode,
      title,
      index,
      difficulty,
      seed,
      grid,
      constraints,
      goals,
      ...(optionalGoals === undefined ? {} : { optionalGoals }),
      ...(rules === undefined ? {} : { rules }),
    },
  };
}

/** `DATA-024`: everything level select needs, without fetching a level file. */
export function validateLevelIndex(value: unknown): Validated<LevelIndex> {
  const c = new Checker();

  if (!isRecord(value)) {
    c.add('', `expected an object, got ${describe(value)}`);
    return { ok: false, faults: c.faults };
  }

  const gameId = c.text(value.gameId, 'gameId');
  const schemaVersion = c.text(value.schemaVersion, 'schemaVersion');

  const rawLevels = value.levels;
  if (!isArray(rawLevels)) {
    c.add('levels', `expected an array, got ${describe(rawLevels)}`);
    return { ok: false, faults: c.faults };
  }

  const seen = new Set<string>();
  const levels: LevelIndex['levels'] = [];

  rawLevels.forEach((raw, i) => {
    const path = `levels[${String(i)}]`;
    if (!isRecord(raw)) {
      c.add(path, `expected an object, got ${describe(raw)}`);
      return;
    }

    const id = c.text(raw.id, `${path}.id`);
    const title = c.text(raw.title, `${path}.title`);
    const file = c.text(raw.file, `${path}.file`);
    const difficulty = c.int(raw.difficulty, `${path}.difficulty`, 1, 10);
    const mode = c.mode(raw.mode, `${path}.mode`);
    const index =
      mode === null ? null : c.int(raw.index, `${path}.index`, MODE_TRAITS[mode].ordered ? 1 : 0);

    // `DATA-011`, checked here and not only in the level-data test, because the
    // index is what level select renders from: two entries sharing a mode and an
    // ordering position produce two tiles both claiming to be level 3.
    if (mode !== null && index !== null) {
      const key = `${mode}:${String(index)}`;
      if (seen.has(key)) c.add(`${path}.index`, `duplicate ordering position ${key}`);
      seen.add(key);
    }

    if (
      id !== null &&
      title !== null &&
      file !== null &&
      difficulty !== null &&
      mode !== null &&
      index !== null
    ) {
      levels.push({ id, index, title, difficulty, mode, file });
    }
  });

  if (c.faults.length > 0 || gameId === null || schemaVersion === null) {
    return { ok: false, faults: c.faults };
  }

  return { ok: true, value: { gameId, schemaVersion, levels } };
}

/** For log lines and error detail. `DATA-049`. */
export function describeFaults(faults: Fault[]): string {
  return faults.map((f) => (f.path === '' ? f.problem : `${f.path}: ${f.problem}`)).join('; ');
}
